/**
 * Global host for companion action execution.
 *
 * Mounted ONCE in app-layout.tsx (inside CompanionActionProvider and
 * CompanionProvider). Reads the currently registered page callbacks from the
 * action-registration-store and passes them to useCompanionActions.
 *
 * This is the ONLY caller of useCompanionActions. Pages register their
 * callbacks via useRegisterCompanionActions() instead of mounting
 * useCompanionActions directly — this avoids the double-subscription problem
 * (useCompanionActions assumes exactly one mounted subscriber).
 *
 * Phase 2 — global navigate callback:
 * useNavigate() is obtained here (inside the router) and merged with the page
 * callbacks before passing to useCompanionActions. This means navigate_to works
 * on ALL pages, even those that haven't registered any page-specific callbacks.
 * When page callbacks are null the host still passes { navigate } so the tool
 * can execute.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { setRouteCatalog, setNodeCatalog, setGroundingProvider } from '@edge/edge-ai'
import { registerAllNodeSchemas } from '@/features/backtest/schemas/register-all'
import { generateNodeCatalog } from '@/features/backtest/graph-engine/catalog'
import { buildGroundingContext } from '@/features/backtest/cookbook/grounding'
import { useCompanionActions } from './use-companion-actions'
import { useActionRegistrationStore } from './action-registration-store'
import { routeCatalogPromptText } from './route-catalog'
import type { ActionCallbacks } from './types'
import { EDGESCRIPT_ENABLED } from '@/features/edgescript/flags'
import {
  loadDrawFromScriptTool,
  loadScriptTools,
} from '@/features/edgescript/companion/lazy-script-tools'
import { loadChartTools } from '@/features/charts/companion/lazy-chart-tools'
import { FLAGS } from '@/services/analytics/feature-flags'
import { useFeatureFlag } from '@/services/analytics/use-feature-flag'

export function CompanionActionHost() {
  const navigate = useNavigate()
  const pageCallbacks = useActionRegistrationStore((s) => s.callbacks)
  const edgeScriptReleased = useFeatureFlag(FLAGS.EDGESCRIPT)
  const chartDrawingReleased = useFeatureFlag(FLAGS.AI_CHART_DRAWING)

  // The browser-side EdgeScript tools register only inside the build gate AND with
  // `edgescript-release` on, once the flag has loaded. Registration is idempotent.
  useEffect(() => {
    if (EDGESCRIPT_ENABLED && edgeScriptReleased) void loadScriptTools?.()
  }, [edgeScriptReleased])

  // The chart drawing tools register with `aichartdrawing-release` on; their
  // EdgeScript half (draw_from_script) also needs the EdgeScript gate and flag.
  useEffect(() => {
    if (!chartDrawingReleased) return
    void loadChartTools()
    if (EDGESCRIPT_ENABLED && edgeScriptReleased) void loadDrawFromScriptTool?.()
  }, [chartDrawingReleased, edgeScriptReleased])

  // The route catalog must be injected from a global mount point — navigate_to
  // is available on every page, so the prompt text can't wait for a page-local
  // setter (the node catalog, by contrast, is only needed on the backtest
  // editor and is set there).
  useEffect(() => {
    setRouteCatalog(routeCatalogPromptText())
    registerAllNodeSchemas()
    setNodeCatalog(generateNodeCatalog())
    setGroundingProvider(buildGroundingContext)
  }, [])

  // Merge: page callbacks (if any) spread with the global navigate override.
  // When page callbacks are null we still produce a non-null object so that
  // navigate_to executes on unwired pages without triggering the early-return
  // guard in useCompanionActions.
  const mergedCallbacks: ActionCallbacks = pageCallbacks
    ? { ...pageCallbacks, navigate }
    : { navigate }

  useCompanionActions(mergedCallbacks)
  return null
}
